import { useState } from 'react'
import { Trash2, Edit, Users, Mail, Phone, Briefcase, RefreshCw, CheckCircle, XCircle, DollarSign } from 'lucide-react'
import { Pessoa } from '../tipos/pessoa'
import { useDeletePessoa } from '../hooks/usePessoa'
import { usePagination } from '../../../hooks/usePagination'
import { PaginationControls } from '../../../componentesCompartilhados/paginacao/PaginationControls'

interface PessoaListProps {
  pessoas: Pessoa[]
  isLoading: boolean
  onRefresh: () => void
  onEditPessoa: (pessoa: Pessoa) => void
}

export function PessoaList({ pessoas, isLoading, onRefresh, onEditPessoa }: PessoaListProps) {
  const deleteMutation = useDeletePessoa()
  const [pessoaSelecionada, setPessoaSelecionada] = useState<Pessoa | null>(null)

  const {
    paginatedItems: pessoasPaginadas,
    currentPage,
    totalPages,
    startIndex,
    endIndex,
    nextPage,
    previousPage,
  } = usePagination(pessoas, {
    totalItems: pessoas.length,
    itemsPerPage: 5,
  })

  const handleDelete = (pessoa: Pessoa) => {
    setPessoaSelecionada(null)
    if (!pessoa.idPessoa) return
    deleteMutation.mutate(pessoa.idPessoa)
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando pessoas...</p>
          <p className="text-sm text-gray-500">Aguarde enquanto buscamos os dados</p>
        </div>
      </div>
    )
  }

  if (!pessoas.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm">
        <div className="p-8 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma pessoa encontrada</h3>
          <p className="text-gray-600 mb-1">Cadastre uma pessoa para começar.</p>
          <p className="text-sm text-gray-500">Utilize o botão "Nova Pessoa" para iniciar.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-success-500 to-brand-500 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Pessoas Cadastradas</h3>
            <p className="text-sm text-gray-600">{pessoas.length} pessoa(s) encontrada(s)</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
          title="Atualizar"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Pessoa
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contato
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cargo
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contratação
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valor Hora
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pessoasPaginadas.map((pessoa) => (
              <tr key={pessoa.idPessoa} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center font-semibold">
                      {pessoa.nome.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{pessoa.nome}</div>
                      {pessoa.observacao && (
                        <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">{pessoa.observacao}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {pessoa.email}
                  </div>
                  {pessoa.telefone && (
                    <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {pessoa.telefone}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {pessoa.cargoNome ? (
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{pessoa.cargoNome}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">Sem cargo</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {pessoa.tipoContratacaoDescricao ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                      {pessoa.tipoContratacaoDescricao}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  {pessoa.valorHoraAtual != null ? (
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-900">
                      <DollarSign className="w-3.5 h-3.5 text-green-500" />
                      {pessoa.valorHoraAtual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {pessoa.ativo ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-success-700 bg-success-100 rounded-full">
                      <CheckCircle className="w-4 h-4" />
                      Ativo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                      <XCircle className="w-4 h-4" />
                      Inativo
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditPessoa(pessoa)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPessoaSelecionada(pessoa)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={pessoas.length}
        startIndex={startIndex}
        endIndex={endIndex}
        onPreviousPage={previousPage}
        onNextPage={nextPage}
        itemLabel="pessoa"
      />

      {/* Modal simples de confirmação */}
      {pessoaSelecionada && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir pessoa</h3>
            <p className="text-sm text-gray-600 mb-4">
              Tem certeza que deseja excluir a pessoa <strong>{pessoaSelecionada.nome}</strong>? Essa ação não poderá
              ser desfeita.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPessoaSelecionada(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(pessoaSelecionada)}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
